import { FC, useMemo } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import {
  Box,
  Divider,
  HStack,
  ListItem,
  Stack,
  UnorderedList,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';

import { formatAmount } from 'common/formatAmount';
import webView from 'common/webView';
import Accordion from 'components/_core/Accordion';
import Callout from 'components/_core/Callout';
import * as Form from 'components/_core/form';
import Heading from 'components/_core/Heading';
import Text from 'components/_core/Text';
import Skeleton from 'components/Skeleton';
import useToast from 'components/ToastProvider/useToast';
import { formatPeriodDate } from 'features/Accounting/pages/Estimation/common/utils';
import TaxStatus from 'features/Accounting/pages/Estimation/components/TaxStatus';
import { UpdateTaxDataDocument } from 'features/Accounting/pages/Estimation/graphql/updateTaxData.gql';

import StepLayout from '../../layout/Step';
import { StepComponentProps } from '../types';
import InputField from './components/InputField';
import RevenueHeader from './components/RevenueHeader';
import { useTaxForecastPeriodRevenueQueryFilters } from './hooks/useTaxForecastPeriodRevenueQueryFilters';
import { useTaxForecastRevenue } from './hooks/useTaxForecastRevenue';
import { locales } from './locales';
import {
  RevenueSituationSchema,
  revenueSituationSchema,
} from './revenueSituation.schema';

type SavePayload = {
  bicDistributionAmount: number | null;
  bncDistributionAmount: number | null;
  complementaryRevenue: number;
  sellingDistributionAmount: number | null;
};

interface RevenueSituationProps extends StepComponentProps<SavePayload> {
  onRevenueFetched: (revenue: number) => void;
}

//TODO: update the complementaryRevenue if the mixActivity amount > revenue + complementaryRevenue
const RevenueSituation: FC<RevenueSituationProps> = ({
  context,
  onBack,
  onNext,
  onRevenueFetched,
  onSave,
}) => {
  const { showToast } = useToast();
  const { stepProgress, taxForecastPeriod, taxForecastProfile } = context;
  const { isInWebView } = webView;

  const filters = useTaxForecastPeriodRevenueQueryFilters({
    // @ts-expect-error Need to fix typing, taxForecastPeriod is always defined on this step
    transactionAfter: taxForecastPeriod?.periodStartsAt,
    // @ts-expect-error Need to fix typing, taxForecastPeriod is always defined on this step
    transactionBefore: taxForecastPeriod?.periodEndsAt,
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setValue,
    trigger,
    watch,
  } = useForm<RevenueSituationSchema>({
    defaultValues: {
      bicAmount: taxForecastPeriod?.bicDistributionAmount,
      bncAmount: taxForecastPeriod?.bncDistributionAmount,
      complementaryRevenue: taxForecastPeriod?.complementaryRevenue,
      isMixedActivityChecked: Boolean(
        taxForecastPeriod?.bicDistributionAmount ||
          taxForecastPeriod?.bncDistributionAmount ||
          taxForecastPeriod?.sellingDistributionAmount,
      ),
      revenue: taxForecastPeriod?.revenue ?? 0,
      sellingAmount: taxForecastPeriod?.sellingDistributionAmount,
    },
    resolver: zodResolver(revenueSituationSchema),
  });

  const { loading: transactionsSumLoading } = useTaxForecastRevenue({
    companyProfileId: context.companyProfile.companyProfileId,
    filters,
    onCompleted: ({ transactionsSum }) => {
      setValue('revenue', transactionsSum);
      onRevenueFetched(transactionsSum);
    },
  });

  const [updateTaxData, { loading: updateTaxDataLoading }] = useMutation(
    UpdateTaxDataDocument,
    { onError: () => showToast({ message: locales.error, type: 'error' }) },
  );

  const [revenue, complementaryRevenue] = watch([
    'revenue',
    'complementaryRevenue',
  ]);

  /**
   * We can't use the computedRevenue field here as we're fetching the current revenue separately in this component
   * and it might be different than what was used in the last tax computation
   */
  const withVatFormatted = useMemo<string>(() => {
    const total = revenue + (complementaryRevenue || 0);

    return formatAmount({ forcePlusSign: false, value: total });
  }, [revenue, complementaryRevenue]);

  const withoutVatFormatted = useMemo<string>(() => {
    const withTaxesTotal = revenue + (complementaryRevenue || 0);
    let withoutTaxesTotal = 0;

    // Use the vatRate to calculate new total withot VAT, otherwise use the vatAmount (for back compatibility)
    if (taxForecastPeriod?.vatRate) {
      const withoutTaxesAmount =
        withTaxesTotal / (1 + taxForecastPeriod.vatRate);
      const vat = withTaxesTotal - withoutTaxesAmount;
      const vatTotal = vat + (taxForecastPeriod?.vatOffset || 0);

      withoutTaxesTotal = withTaxesTotal - vatTotal;
    } else {
      withoutTaxesTotal = withTaxesTotal - (taxForecastPeriod?.vatAmount || 0);
    }

    return formatAmount({ forcePlusSign: false, value: withoutTaxesTotal });
  }, [
    complementaryRevenue,
    revenue,
    taxForecastPeriod?.vatAmount,
    taxForecastPeriod?.vatOffset,
    taxForecastPeriod?.vatRate,
  ]);

  const isSubjectToVat = Boolean(taxForecastProfile?.isSubjectToVat);

  const onSubmit: SubmitHandler<RevenueSituationSchema> = async (data) => {
    const input: SavePayload = {
      bicDistributionAmount: null,
      bncDistributionAmount: null,
      complementaryRevenue: data.complementaryRevenue || 0,
      sellingDistributionAmount: null,
    };

    if (data.isMixedActivityChecked) {
      input.bicDistributionAmount = data.bicAmount;
      input.bncDistributionAmount = data.bncAmount;
      input.sellingDistributionAmount = data.sellingAmount;
    }

    await updateTaxData({
      onCompleted: () => {
        onSave(input);
        onNext();
      },
      variables: {
        input: {
          companyProfileId: context.companyProfile.companyProfileId,
          taxForecastPeriodData: {
            activityDistributionAmounts: {
              bicDistributionAmount: input.bicDistributionAmount,
              bncDistributionAmount: input.bncDistributionAmount,
              sellingDistributionAmount: input.sellingDistributionAmount,
            },
            complementaryRevenue: input.complementaryRevenue,
            taxForecastPeriodId: taxForecastPeriod?.taxForecastPeriodId,
          },
          taxFrequency: context.companyProfile.taxFrequency,
        },
      },
    });
  };

  return (
    <StepLayout>
      <StepLayout.Header onBack={onBack} stepProgress={stepProgress} />

      <StepLayout.Body padding={isInWebView ? 'space-0' : 'space-16'}>
        <Box paddingX={isInWebView ? 'space-16' : undefined}>
          <Stack>
            <Heading size="lg">{locales.title}</Heading>

            {!isInWebView ? <Text size="lg">{locales.subtitle}</Text> : null}
          </Stack>

          <HStack align="center" gap="space-8" marginY="space-24">
            <Text fontWeight="weight-500" variant="primary">
              {formatPeriodDate(
                taxForecastPeriod?.periodStartsAt ?? 0,
                taxForecastPeriod?.periodEndsAt ?? 0,
              )}
            </Text>

            <TaxStatus
              isPaid={taxForecastPeriod?.isPaid ?? false}
              payBefore={taxForecastPeriod?.payBefore ?? 0}
            />
          </HStack>
          <RevenueHeader
            isSubjectToVat={isSubjectToVat}
            revenue={withVatFormatted}
            revenueWithoutVat={withoutVatFormatted}
            transactionsSumLoading={transactionsSumLoading}
          />
        </Box>
        {isInWebView ? (
          <Box backgroundColor="grey.300" height="space-6" marginY="space-32" />
        ) : null}

        <Box paddingX={isInWebView ? 'space-16' : undefined}>
          <HStack
            alignItems={isInWebView ? 'start' : 'center'}
            flexDirection={isInWebView ? 'column' : 'row'}
            gap="space-4"
            justifyContent="space-between"
            marginBottom="space-24"
          >
            <Text fontSize="small" variant="secondary">
              {locales.form.label.revenue}
            </Text>
            <Skeleton display="flex" isLoaded={!transactionsSumLoading}>
              <Text
                fontWeight="weight-500"
                marginRight={isInWebView ? 'space-2' : 'space-20'}
                variant="primary"
              >
                {taxForecastPeriod?.revenue ?? 0}
              </Text>
              <Text
                fontWeight="weight-500"
                variant="primary"
                whiteSpace="nowrap"
              >
                {locales.withVatCurrency}
              </Text>
            </Skeleton>
          </HStack>

          <InputField
            inputProps={register('complementaryRevenue', {
              deps: ['bicAmount', 'bncAmount', 'sellingAmount'],
              valueAsNumber: true,
            })}
            label={locales.form.label.complementaryRevenue}
            placeholder="+/- 0"
          />
        </Box>

        <Divider marginBottom="space-32" marginTop="space-8" />

        <Box paddingX={isInWebView ? 'space-16' : undefined}>
          <Controller
            control={control}
            name="isMixedActivityChecked"
            render={({ field }) => {
              const handleChange = () => {
                field.onChange(!field.value);

                if (!field.value === false) {
                  trigger();
                }
              };

              return (
                <>
                  <HStack
                    alignItems="baseline"
                    cursor="pointer"
                    marginBottom="space-24"
                  >
                    <Form.Checkbox
                      gap={0}
                      isChecked={field.value}
                      onChange={handleChange}
                    />

                    <Text onClick={handleChange} variant="primary">
                      {locales.form.label.mixActivity}
                    </Text>
                  </HStack>

                  {errors?.sellingAmount?.message ||
                  errors?.bicAmount?.message ||
                  errors?.bncAmount?.message ? (
                    <Callout
                      fontSize="small"
                      marginBottom="space-24"
                      variant="error"
                    >
                      {locales.formatString(locales.form.distributionAmounts, {
                        amount: withVatFormatted,
                      })}
                    </Callout>
                  ) : null}

                  {field.value ? (
                    <>
                      <InputField
                        error={errors?.bicAmount?.message}
                        inputProps={register('bicAmount', {
                          deps: [
                            'complementaryRevenue',
                            'bncAmount',
                            'sellingAmount',
                          ],
                          valueAsNumber: true,
                        })}
                        label={locales.form.label.bic}
                      />
                      <InputField
                        error={errors?.bncAmount?.message}
                        inputProps={register('bncAmount', {
                          deps: [
                            'complementaryRevenue',
                            'bicAmount',
                            'sellingAmount',
                          ],
                          valueAsNumber: true,
                        })}
                        label={locales.form.label.bnc}
                      />
                      <InputField
                        error={errors?.sellingAmount?.message}
                        inputProps={register('sellingAmount', {
                          deps: [
                            'complementaryRevenue',
                            'bicAmount',
                            'bncAmount',
                          ],
                          valueAsNumber: true,
                        })}
                        label={locales.form.label.selling}
                      />
                    </>
                  ) : null}
                </>
              );
            }}
          />
        </Box>
      </StepLayout.Body>

      <StepLayout.Sidebar>
        <Heading marginBottom="space-16">{locales.faq.title}</Heading>

        <Accordion allowMultiple allowToggle={undefined}>
          <Accordion.Item title={locales.faq.question1.title}>
            <Accordion.Text>{locales.faq.question1.subtitle}</Accordion.Text>
          </Accordion.Item>

          <Accordion.Item title={locales.faq.question2.title}>
            <Accordion.Text>{locales.faq.question2.subtitle}</Accordion.Text>
            <UnorderedList>
              <ListItem>
                <Text variant="light">{locales.faq.question2.bic}</Text>
              </ListItem>

              <ListItem>
                <Text variant="light">{locales.faq.question2.bnc}</Text>
              </ListItem>

              <ListItem>
                <Text variant="light">{locales.faq.question2.craft}</Text>
              </ListItem>
            </UnorderedList>
            <Accordion.Text>{locales.faq.question2.footer}</Accordion.Text>
          </Accordion.Item>
        </Accordion>
      </StepLayout.Sidebar>

      <StepLayout.Footer stepProgress={stepProgress}>
        <StepLayout.FooterButton
          isDisabled={transactionsSumLoading}
          isLoading={updateTaxDataLoading}
          onClick={handleSubmit(onSubmit)}
        >
          {isSubjectToVat ? locales.withVatSubmit : locales.submit}
        </StepLayout.FooterButton>
      </StepLayout.Footer>
    </StepLayout>
  );
};

export default RevenueSituation;
